import React, { useEffect, useState } from 'react'
import { Router } from 'react-router'
import { Offline, Online } from 'react-detect-offline'
import { Toaster } from 'react-hot-toast'

import { AdminPanel, ChatManager } from '@core'
import { Loader } from '@ui'
import { isValue, miscUtil, routeUtil } from '@app/util'
import requestMeta from '@app/request/meta'

import OfflinePage from './component/pages/other/offline'
import './style/index.scss'
import './dayswaps.scss'
import './favicons'
import connect from './connect'
import { Routes } from './routing'
import Websockets from './websockets'
import { useRootDispatch } from './store'
import { setMeta } from './reducer/meta'

/**
 * Reloading out of a stale bundle has to be repeatable across boots — one attempt is not
 * enough when the browser keeps handing back a cached index.html — but not unbounded, or a
 * client that can never reach the new build would reload forever.
 */
const RELOAD_ATTEMPTS_KEY = 'ds-version-reload-attempts'
const MAX_RELOAD_ATTEMPTS = 3

const reloadAttempts = () => {
  try {
    return parseInt(window.sessionStorage.getItem(RELOAD_ATTEMPTS_KEY), 10) || 0
  } catch {
    // storage is unavailable in some privacy modes — treat it as exhausted rather than
    // take a reload we have no way of counting
    return MAX_RELOAD_ATTEMPTS
  }
}

const rememberReloadAttempt = (attempts) => {
  try {
    window.sessionStorage.setItem(RELOAD_ATTEMPTS_KEY, String(attempts + 1))
  } catch {}
}

const forgetReloadAttempts = () => {
  try {
    window.sessionStorage.removeItem(RELOAD_ATTEMPTS_KEY)
  } catch {}
}

const Dayswaps = ({
  login,
  auth,
  logout,
  locale
}) => {
  const [loading, setLoading] = useState(true)
  const dispatch = useRootDispatch()

  const loadMeta = async () => {
    const res = await requestMeta()

    // check for null returned due to old, invalid auth token
    if (!isValue(res)) {
      console.log('Reseting the store and auth token.')
      logout(true, false, false)
      return
    }

    await dispatch(setMeta(res))

    if (!res?.frontend) return
    const currentCommitStorage = window.localStorage.getItem('ds-current-commit')
    const attempts = reloadAttempts()

    if (
      isValue(currentCommitStorage) &&
      currentCommitStorage < res.frontend.time &&
      attempts < MAX_RELOAD_ATTEMPTS &&
      window.location.hostname !== 'localhost'
    ) {
      console.log('Version in localStorage is older than the current version at the server. Reloading the app.')
      rememberReloadAttempt(attempts)
      window.location.reload()
      return
    }

    // Record the server's version only once the client is actually running it. Writing it
    // up front made the reload one-shot per deploy: when that single reload came back as
    // the cached old bundle, every later boot compared equal, skipped the reload and left
    // the client on a build whose chunk files the server no longer has.
    window.localStorage.setItem('ds-current-commit', res.frontend.time)
    forgetReloadAttempts()

    if (auth && !window.location.href.includes('auth')) {
      await login({ auth })
    }
  }

  useEffect(() => {
    loadMeta().finally(() => {
      setLoading(false)
    })
  }, [])
  useEffect(() => { /* Way to make app rerender on locale change */ }, [locale])

  if (loading) return <Loader size={Loader.SIZES.LARGE} />

  return (
    <ChatManager key={locale}>
      <AdminPanel />
      <Online polling={{ enabled: false }}>
        {(!miscUtil.isTerminalInterface()) && <Websockets />}
        <Router history={routeUtil.history}>
          <Routes />
        </Router>
      </Online>
      <Offline polling={{ enabled: false }}>
        <OfflinePage />
      </Offline>
      <Toaster position='top-right' />
    </ChatManager>
  )
}

export default connect(Dayswaps)
